import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Zap, PhoneCall, Bot, Clock, TrendingUp } from 'lucide-react';
import {
  useAllDialerSessions,
  useAllDialerAttempts,
  formatDuration,
} from '@/hooks/useDialerAnalytics';

const RANGES = [
  { label: 'Hoje', days: 1 },
  { label: '7 dias', days: 7 },
  { label: '30 dias', days: 30 },
];

export default function PowerDialerDashboard() {
  const [days, setDays] = useState(7);
  const { data: sessions = [], isLoading } = useAllDialerSessions(days);
  const { data: attempts = [], isLoading: loadingAttempts } = useAllDialerAttempts(days);

  const sdrName = (s: (typeof sessions)[number]) =>
    s.sdr?.display_name ||
    [s.sdr?.first_name, s.sdr?.last_name].filter(Boolean).join(' ') ||
    s.sdr?.email ||
    'SDR';

  const attemptsBySdr = useMemo(() => {
    const map = new Map<string, { dialed: number; human: number; machine: number; connectSecs: number[]; talk: number }>();
    for (const a of attempts) {
      const key = a.sdr_id ?? 'unknown';
      const entry = map.get(key) ?? { dialed: 0, human: 0, machine: 0, connectSecs: [], talk: 0 };
      entry.dialed += 1;
      if (a.outcome === 'human') entry.human += 1;
      if (a.outcome === 'machine') entry.machine += 1;
      if (a.connected_at) {
        const delta = (new Date(a.connected_at).getTime() - new Date(a.created_at).getTime()) / 1000;
        if (Number.isFinite(delta) && delta >= 0) entry.connectSecs.push(delta);
      }
      entry.talk += a.duration_seconds ?? 0;
      map.set(key, entry);
    }
    return map;
  }, [attempts]);

  const rows = useMemo(() => {
    const bySdr = new Map<string, { name: string; sessions: number; lastAt: string | null }>();
    for (const s of sessions) {
      const key = s.sdr_id;
      const entry = bySdr.get(key) ?? { name: sdrName(s), sessions: 0, lastAt: null };
      entry.sessions += 1;
      const at = s.started_at ?? s.created_at;
      if (!entry.lastAt || new Date(at) > new Date(entry.lastAt)) entry.lastAt = at;
      bySdr.set(key, entry);
    }
    return Array.from(bySdr.entries())
      .map(([sdrId, info]) => {
        const a = attemptsBySdr.get(sdrId) ?? { dialed: 0, human: 0, machine: 0, connectSecs: [], talk: 0 };
        return {
          sdrId,
          ...info,
          ...a,
          humanRate: a.dialed > 0 ? (a.human / a.dialed) * 100 : 0,
          avgConnect: a.connectSecs.length
            ? a.connectSecs.reduce((x, y) => x + y, 0) / a.connectSecs.length
            : null,
        };
      })
      .sort((x, y) => y.human - x.human);
  }, [sessions, attemptsBySdr]);

  const totals = useMemo(() => {
    const dialed = attempts.length;
    const human = attempts.filter((a) => a.outcome === 'human').length;
    const machine = attempts.filter((a) => a.outcome === 'machine').length;
    const talk = attempts.reduce((sum, a) => sum + (a.duration_seconds ?? 0), 0);
    return { dialed, human, machine, talk, humanRate: dialed ? (human / dialed) * 100 : 0 };
  }, [attempts]);

  const loading = isLoading || loadingAttempts;

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-500" /> Power Dialer — Visão executiva
          </h1>
          <p className="text-sm text-muted-foreground">Desempenho de discagem por SDR</p>
        </div>
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={days === r.days ? 'default' : 'outline'}
              className="h-8 rounded-full"
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <PhoneCall className="h-3.5 w-3.5" /> Discadas
            </div>
            <div className="text-2xl font-semibold mt-1">{totals.dialed}</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Humanos</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-600">{totals.human}</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Bot className="h-3.5 w-3.5" /> Caixa postal
            </div>
            <div className="text-2xl font-semibold mt-1 text-amber-600">{totals.machine}</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3.5 w-3.5" /> Taxa humana
            </div>
            <div className="text-2xl font-semibold mt-1">{totals.humanRate.toFixed(0)}%</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> Tempo em conversa
            </div>
            <div className="text-2xl font-semibold mt-1">{formatDuration(totals.talk)}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Ranking por SDR</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center text-sm text-muted-foreground p-6">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Carregando...
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">Nenhuma sessão no período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left font-medium px-4 py-2 w-[26%]">SDR</th>
                    <th className="text-right font-medium px-3 py-2 w-[10%]">Sessões</th>
                    <th className="text-right font-medium px-3 py-2 w-[10%]">Discadas</th>
                    <th className="text-right font-medium px-3 py-2 w-[10%]">Humanos</th>
                    <th className="text-right font-medium px-3 py-2 w-[12%]">Caixa postal</th>
                    <th className="text-right font-medium px-3 py-2 w-[10%]">Taxa</th>
                    <th className="text-right font-medium px-3 py-2 w-[11%]">Até conectar</th>
                    <th className="text-right font-medium px-3 py-2 w-[11%]">Conversa</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.sdrId} className="border-b last:border-0">
                      <td className="px-4 py-2 truncate">{r.name}</td>
                      <td className="px-3 py-2 text-right">{r.sessions}</td>
                      <td className="px-3 py-2 text-right">{r.dialed}</td>
                      <td className="px-3 py-2 text-right text-emerald-600 font-medium">{r.human}</td>
                      <td className="px-3 py-2 text-right">{r.machine}</td>
                      <td className="px-3 py-2 text-right">{r.humanRate.toFixed(0)}%</td>
                      <td className="px-3 py-2 text-right">{formatDuration(r.avgConnect)}</td>
                      <td className="px-3 py-2 text-right">{formatDuration(r.talk)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sessões recentes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sessions.slice(0, 25).map((s) => (
            <Link
              key={s.id}
              to={`/outbound/discador/sessao/${s.id}`}
              className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 hover:bg-muted/50 transition-colors flex-wrap"
            >
              <div>
                <div className="text-sm font-medium">{sdrName(s)}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(s.started_at ?? s.created_at).toLocaleString('pt-BR')} · {s.total_dialed} discadas ·{' '}
                  {s.total_human} humanos
                </div>
              </div>
              <Badge variant="outline" className="rounded-full text-xs">
                {s.status === 'active' ? 'Em andamento' : s.status === 'paused' ? 'Pausada' : 'Encerrada'}
              </Badge>
            </Link>
          ))}
          {!loading && sessions.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhuma sessão no período.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
